import { Currency } from '@sequencehq/api'
import { PriceEditorTier, PriceEditorPercentageTier } from '../domain'

/**
 * Converts pricing table data to TSV format for clipboard copying
 */
export type PricingTableData =
  | {
      tiers: PriceEditorTier[]
      currency: Currency
    }
  | {
      percentageTiers: PriceEditorPercentageTier[]
      currency: Currency
      isGraduated?: boolean // Flag to indicate if this is graduated pricing
      includeLimits?: boolean // Flag to indicate if volume pricing includes fee thresholds
    }

/**
 * Formats a price value for display in TSV
 */
const formatPriceForTsv = (value: string): string => {
  if (!value || value === '0' || value === '0.00') {
    return '0.00'
  }

  try {
    // Simple number formatting for TSV - no currency symbols needed
    const numValue = parseFloat(value)
    return numValue.toFixed(2)
  } catch {
    return value
  }
}

/**
 * Formats a percentage value for display in TSV
 */
const formatPercentageForTsv = (value: string): string => {
  if (!value || value === '0') {
    return '0%'
  }

  return `${value}%`
}

/**
 * Converts regular pricing tiers to TSV format
 */
const convertFixedTiersToTsv = (data: {
  tiers: PriceEditorTier[]
  currency: Currency
}): string => {
  const headers = ['First unit', 'Last unit', 'Unit price', 'Flat fee']
  const headerRow = headers.join('\t')

  // Add defensive check for missing tiers data
  if (!data.tiers || data.tiers.length === 0) {
    return headerRow
  }

  const dataRows = data.tiers.map(tier => {
    const firstUnit = tier.firstUnit || '0'
    const lastUnit = tier.lastUnit === '∞' ? '∞' : tier.lastUnit || '0'
    const unitPrice = formatPriceForTsv(tier.unitPrice || '0')
    const flatFee = formatPriceForTsv(tier.flatFee || '0')

    return [firstUnit, lastUnit, unitPrice, flatFee].join('\t')
  })

  return [headerRow, ...dataRows].join('\n')
}

/**
 * Converts percentage-based pricing tiers to TSV format
 */
const convertPercentageTiersToTsv = (data: {
  percentageTiers: PriceEditorPercentageTier[]
  currency: Currency
  isGraduated?: boolean
  includeLimits?: boolean
}): string => {
  // Determine which columns to include:
  // - Graduated: always exclude min/max price (not user-editable)
  // - Volume: include min/max price only if fee thresholds are enabled
  const shouldIncludeLimits = !data.isGraduated && data.includeLimits
  const headers = shouldIncludeLimits
    ? ['First unit', 'Last unit', '% fee', 'Min price', 'Max price', 'Flat fee']
    : ['First unit', 'Last unit', '% fee', 'Flat fee']
  const headerRow = headers.join('\t')

  // Add defensive check for missing percentage tiers data
  if (!data.percentageTiers || data.percentageTiers.length === 0) {
    return headerRow
  }

  const dataRows = data.percentageTiers.map(tier => {
    const firstUnit = tier.firstUnit || '0'
    const lastUnit = tier.lastUnit === '∞' ? '∞' : tier.lastUnit || '0'
    const percentage = formatPercentageForTsv(tier.unitPercentage || '0')
    const flatFee = formatPriceForTsv(tier.flatFee || '0')

    if (shouldIncludeLimits) {
      // Volume with fee thresholds: include all columns
      const minPrice = formatPriceForTsv(tier.minPrice || '0')
      const maxPrice = formatPriceForTsv(tier.maxPrice || '0')
      return [
        firstUnit,
        lastUnit,
        percentage,
        minPrice,
        maxPrice,
        flatFee
      ].join('\t')
    } else {
      // Graduated or Volume without fee thresholds: exclude min/max price columns
      return [firstUnit, lastUnit, percentage, flatFee].join('\t')
    }
  })

  return [headerRow, ...dataRows].join('\n')
}

/**
 * Main function to convert pricing table data to TSV format
 */
export const pricingTableToTsv = (data: PricingTableData): string => {
  if ('tiers' in data) {
    return convertFixedTiersToTsv(data)
  } else {
    return convertPercentageTiersToTsv(data)
  }
}

/**
 * Copies pricing table data to clipboard in TSV format
 */
export const copyPricingTableToClipboard = async (
  data: PricingTableData
): Promise<boolean> => {
  try {
    const tsvData = pricingTableToTsv(data)
    await navigator.clipboard.writeText(tsvData)
    return true
  } catch (error) {
    console.error('Failed to copy pricing table to clipboard:', error)
    return false
  }
}
